'use client';

import { CheckSquare, ChevronRight } from 'lucide-react';
import {
  type SearchResult,
  searchResultSubtitle,
  searchResultValue,
} from '../../lib/search-result';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { CommandItem } from '../../ui/command';
import { Kbd } from '../../ui/kbd';
import { getUserInitials } from '../../ui/user-avatar';

export interface SearchResultItemProps {
  result: SearchResult;
  /** Primary action — navigate to the entity. */
  onSelect: () => void;
  /**
   * Secondary action, when the result has something to drill into. Rendering the hint is
   * conditional on this being supplied, so a row never advertises a key that does nothing.
   */
  onDrillIn?: () => void;
}

export function SearchResultItem({ result, onSelect, onDrillIn }: SearchResultItemProps) {
  const subtitle = searchResultSubtitle(result);

  return (
    <CommandItem value={searchResultValue(result)} onSelect={onSelect} className="gap-3">
      {result.entityType === 'contact' ? (
        <Avatar className="size-6 shrink-0">
          {result.avatarUrl && <AvatarImage src={result.avatarUrl} alt="" />}
          <AvatarFallback className="text-[10px]">
            {getUserInitials({ fullName: result.title })}
          </AvatarFallback>
        </Avatar>
      ) : (
        <span className="bg-muted text-muted-foreground flex size-6 shrink-0 items-center justify-center rounded">
          <CheckSquare className="size-3.5" />
        </span>
      )}

      <span className="flex min-w-0 flex-col">
        <span
          className={
            // A completed task stays findable but reads as done rather than actionable.
            result.entityType === 'task' && result.isTerminal
              ? 'truncate text-muted-foreground line-through'
              : 'truncate'
          }
        >
          {result.title}
        </span>
        {subtitle && <span className="text-muted-foreground truncate text-xs">{subtitle}</span>}
      </span>

      {onDrillIn && (
        <span className="text-muted-foreground ml-auto flex shrink-0 items-center gap-1.5 text-xs">
          {result.entityType === 'contact' && result.openTaskCount > 0 && (
            <span>
              {result.openTaskCount} open {result.openTaskCount === 1 ? 'task' : 'tasks'}
            </span>
          )}
          <Kbd>tab</Kbd>
          <ChevronRight className="size-3" />
        </span>
      )}
    </CommandItem>
  );
}
